import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOff,
  PhoneMissed,
  Users,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  Play,
  Pause,
  Volume2,
  User,
  ArrowUpRight,
  RefreshCw,
  Settings,
  Loader2,
  Bot,
  UserCheck,
  History,
  Filter,
  Search,
  ChevronDown,
  ExternalLink,
  Copy,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status, tier }) => {
  const statusConfig = {
    waiting: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Waiting' },
    in_progress: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'In Progress' },
    completed: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Completed' },
    abandoned: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Abandoned' },
    resolved: { color: 'bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30', label: 'Resolved' },
    escalated: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'Escalated' },
    pending: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', label: 'Pending' }
  };

  const config = statusConfig[status] || statusConfig.waiting;
  
  return (
    <div className="flex items-center space-x-2">
      <span className={`px-2 py-0.5 rounded text-xs border ${config.color}`}>
        {config.label}
      </span>
      {tier && (
        <span className={`px-2 py-0.5 rounded text-xs ${
          tier === 1 ? 'bg-neon-purple/20 text-neon-purple' : 'bg-neon-cyan/20 text-neon-cyan'
        }`}>
          {tier === 1 ? 'AI' : 'Human'}
        </span>
      )}
    </div>
  );
};

// Stats Card
const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass-card p-4"
  >
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm text-slate-400">{label}</div>
        <div className="text-2xl font-bold text-white">{value}</div>
        {subtext && <div className="text-xs text-slate-500">{subtext}</div>}
      </div>
      <div className={`p-3 rounded-lg bg-${color}/20`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
    </div>
  </motion.div>
);

// Call Log Item
const CallLogItem = ({ call }) => {
  const [expanded, setExpanded] = useState(false);
  const duration = call.duration_seconds 
    ? `${Math.floor(call.duration_seconds / 60)}:${String(call.duration_seconds % 60).padStart(2, '0')}`
    : '--:--';
  
  return (
    <div className="glass-card p-4 hover:bg-white/5 transition-colors">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center space-x-4">
          <div className={`p-2 rounded-lg ${
            call.escalated ? 'bg-orange-500/20' : 'bg-neon-purple/20'
          }`}>
            {call.escalated ? (
              <ArrowUpRight className="w-5 h-5 text-orange-400" />
            ) : (
              <Bot className="w-5 h-5 text-neon-purple" />
            )}
          </div>
          <div>
            <div className="font-medium text-white">{call.caller_number}</div>
            <div className="text-sm text-slate-400">
              {new Date(call.start_time).toLocaleString()}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <div className="text-sm text-white">{duration}</div>
            <StatusBadge status={call.resolution || 'pending'} tier={call.tier} />
          </div>
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="mt-4 pt-4 border-t border-white/10"
        >
          {call.escalation_reason && (
            <div className="mb-3">
              <span className="text-xs text-slate-500">Escalation Reason:</span>
              <p className="text-sm text-white">{call.escalation_reason}</p>
            </div>
          )}
          {call.transcript?.length > 0 && (
            <div>
              <span className="text-xs text-slate-500">Transcript:</span>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {call.transcript.map((msg, idx) => (
                  <div key={idx} className={`text-sm p-2 rounded ${
                    msg.role === 'assistant' 
                      ? 'bg-neon-purple/10 text-neon-purple' 
                      : 'bg-white/5 text-white'
                  }`}>
                    <span className="font-medium">{msg.role === 'assistant' ? 'AI: ' : 'Caller: '}</span>
                    {msg.content}
                  </div>
                ))}
              </div>
            </div>
          )}
          {call.trip_ids_referenced?.length > 0 && (
            <div className="mt-3">
              <span className="text-xs text-slate-500">Trips Referenced:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {call.trip_ids_referenced.map((tripId, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-neon-cyan/20 text-neon-cyan rounded text-xs">
                    {tripId}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// Support Queue Item
const QueueItem = ({ item, onAssign, onResolve }) => {
  const [notes, setNotes] = useState('');
  const [showResolve, setShowResolve] = useState(false);
  
  return (
    <div className="glass-card p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-2 rounded-lg bg-yellow-500/20">
            <PhoneIncoming className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <div className="font-medium text-white">{item.caller_number}</div>
            <div className="text-sm text-slate-400">{item.reason}</div>
            <div className="text-xs text-slate-500">
              {new Date(item.created_at).toLocaleString()}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <StatusBadge status={item.status} />
          {item.status === 'waiting' && (
            <button
              onClick={() => onAssign(item.id, 'current_user')}
              className="px-3 py-1 bg-neon-cyan/20 text-neon-cyan rounded-lg text-sm hover:bg-neon-cyan/30"
            >
              Take Call
            </button>
          )}
          {item.status === 'in_progress' && (
            <button
              onClick={() => setShowResolve(true)}
              className="px-3 py-1 bg-neon-green/20 text-neon-green rounded-lg text-sm hover:bg-neon-green/30"
            >
              Resolve
            </button>
          )}
        </div>
      </div>
      
      {item.context?.ai_transcript && (
        <div className="mt-3 p-3 bg-white/5 rounded-lg">
          <span className="text-xs text-slate-500">AI Summary:</span>
          <p className="text-sm text-slate-300 mt-1">{item.context.ai_transcript}</p>
        </div>
      )}
      
      <AnimatePresence>
        {showResolve && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-4 pt-4 border-t border-white/10"
          >
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add resolution notes..."
              className="w-full input-dark resize-none"
              rows={2}
            />
            <div className="flex justify-end space-x-2 mt-2">
              <button
                onClick={() => setShowResolve(false)}
                className="px-3 py-1 text-slate-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => { onResolve(item.id, notes); setShowResolve(false); }}
                className="px-3 py-1 bg-neon-green text-white rounded-lg"
              >
                Mark Resolved
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Callback Message Item
const CallbackItem = ({ message, onComplete }) => {
  const [notes, setNotes] = useState('');
  const [showComplete, setShowComplete] = useState(false);
  
  return (
    <div className="glass-card p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-2 rounded-lg bg-purple-500/20">
            <MessageSquare className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <div className="font-medium text-white">{message.caller_number}</div>
            <p className="text-sm text-slate-300 mt-1">{message.message}</p>
            <div className="text-xs text-slate-500 mt-1">
              {new Date(message.created_at).toLocaleString()}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <StatusBadge status={message.status} />
          {message.status === 'pending' && (
            <button
              onClick={() => setShowComplete(true)}
              className="px-3 py-1 bg-neon-green/20 text-neon-green rounded-lg text-sm hover:bg-neon-green/30"
            >
              Mark Called
            </button>
          )}
        </div>
      </div>
      
      <AnimatePresence>
        {showComplete && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-4 pt-4 border-t border-white/10"
          >
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Callback notes..."
              className="w-full input-dark resize-none"
              rows={2}
            />
            <div className="flex justify-end space-x-2 mt-2">
              <button
                onClick={() => setShowComplete(false)}
                className="px-3 py-1 text-slate-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => { onComplete(message.id, notes); setShowComplete(false); }}
                className="px-3 py-1 bg-neon-green text-white rounded-lg"
              >
                Complete
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Configuration Panel
const ConfigPanel = ({ config, onRefresh }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    tier2_support_number: '',
    tier2_support_email: '',
    business_hours_start: '09:00',
    business_hours_end: '17:00',
    greeting_message: '',
    after_hours_message: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/phone/settings`);
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setFormData({
          tier2_support_number: data.tier2_support_number || '',
          tier2_support_email: data.tier2_support_email || '',
          business_hours_start: data.business_hours_start || '09:00',
          business_hours_end: data.business_hours_end || '17:00',
          greeting_message: data.greeting_message || '',
          after_hours_message: data.after_hours_message || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/phone/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success('Settings saved successfully');
        fetchSettings();
        onRefresh();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(config?.webhook_url || '');
    toast.success('Webhook URL copied!');
  };
  
  return (
    <div className="space-y-6">
      {/* System Status */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2 text-neon-cyan" />
          System Status
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">SignalWire</span>
              {config?.signalwire_configured ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <AlertCircle className="w-4 h-4 text-yellow-400" />
              )}
            </div>
            <div className="text-white font-medium">
              {config?.signalwire_configured ? 'Connected' : 'Not Configured'}
            </div>
            {config?.space_url && (
              <div className="text-xs text-slate-500 mt-1">{config.space_url}</div>
            )}
          </div>
          
          <div className="p-4 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Phone Number</span>
              <Phone className="w-4 h-4 text-neon-cyan" />
            </div>
            <div className="text-white font-medium">
              {config?.phone_number || 'Not assigned'}
            </div>
          </div>
          
          <div className="p-4 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">AI Assistant</span>
              {config?.ai_enabled ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <AlertCircle className="w-4 h-4 text-yellow-400" />
              )}
            </div>
            <div className="text-white font-medium">
              {config?.ai_enabled ? 'Enabled (GPT)' : 'Disabled'}
            </div>
          </div>
          
          <div className="p-4 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Webhook URL</span>
              <button onClick={copyWebhookUrl} className="p-1 hover:bg-white/10 rounded">
                <Copy className="w-4 h-4 text-slate-400" />
              </button>
            </div>
            <code className="text-xs text-neon-cyan break-all">{config?.webhook_url}</code>
          </div>
        </div>
      </div>

      {/* Editable Settings */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <UserCheck className="w-5 h-5 mr-2 text-neon-purple" />
          Escalation Settings (Tier 2)
        </h3>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-neon-cyan" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Tier 2 Support Phone Number</label>
                <input
                  type="tel"
                  value={formData.tier2_support_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, tier2_support_number: e.target.value }))}
                  placeholder="+1 (555) 123-4567"
                  className="w-full input-dark"
                />
                <p className="text-xs text-slate-500 mt-1">Calls will be transferred here when escalated to human support</p>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Support Email (for notifications)</label>
                <input
                  type="email"
                  value={formData.tier2_support_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, tier2_support_email: e.target.value }))}
                  placeholder="support@medexova.com"
                  className="w-full input-dark"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Business Hours Start</label>
                <input
                  type="time"
                  value={formData.business_hours_start}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_hours_start: e.target.value }))}
                  className="w-full input-dark"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Business Hours End</label>
                <input
                  type="time"
                  value={formData.business_hours_end}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_hours_end: e.target.value }))}
                  className="w-full input-dark"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">AI Greeting Message</label>
              <textarea
                value={formData.greeting_message}
                onChange={(e) => setFormData(prev => ({ ...prev, greeting_message: e.target.value }))}
                placeholder="Thank you for calling MedexOVA. How can I help you today?"
                rows={2}
                className="w-full input-dark resize-none"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">After Hours Message</label>
              <textarea
                value={formData.after_hours_message}
                onChange={(e) => setFormData(prev => ({ ...prev, after_hours_message: e.target.value }))}
                placeholder="Our office is currently closed. Please leave a message..."
                rows={2}
                className="w-full input-dark resize-none"
              />
            </div>

            <div className="flex justify-end pt-4 border-t border-white/10">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-neon-cyan text-slate-900 rounded-lg font-medium hover:bg-neon-cyan/80 flex items-center space-x-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Save Settings</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* SignalWire Setup Instructions */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">SignalWire Setup Instructions</h3>
        <div className="space-y-3 text-sm text-slate-400">
          <p>To complete the phone system setup:</p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Log in to your <a href="https://signalwire.com" target="_blank" rel="noopener noreferrer" className="text-neon-cyan hover:underline">SignalWire dashboard</a></li>
            <li>Go to <strong>Phone Numbers</strong> → Select your number <strong>{config?.phone_number}</strong></li>
            <li>Under <strong>Voice Settings</strong>, set the webhook URL to:
              <code className="block mt-1 p-2 bg-black/30 rounded text-neon-cyan break-all">{config?.webhook_url}</code>
            </li>
            <li>Set the HTTP method to <strong>POST</strong></li>
            <li>Save your changes</li>
          </ol>
          <p className="mt-4 text-slate-500">
            Once configured, incoming calls to {config?.phone_number} will be handled by the AI assistant with escalation to human support when needed.
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Component
export const PhoneSystem = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [callLogs, setCallLogs] = useState([]);
  const [supportQueue, setSupportQueue] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, logsRes, queueRes, callbacksRes, configRes] = await Promise.all([
        fetch(`${API_URL}/api/phone/stats`),
        fetch(`${API_URL}/api/phone/call-logs?limit=20`),
        fetch(`${API_URL}/api/phone/support-queue`),
        fetch(`${API_URL}/api/phone/callback-messages`),
        fetch(`${API_URL}/api/phone/config`)
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (logsRes.ok) {
        const data = await logsRes.json();
        setCallLogs(data.calls || []);
      }
      if (queueRes.ok) {
        const data = await queueRes.json();
        setSupportQueue(data.queue || []);
      }
      if (callbacksRes.ok) {
        const data = await callbacksRes.json();
        setCallbacks(data.messages || []);
      }
      if (configRes.ok) setConfig(await configRes.json());
    } catch (error) {
      console.error('Failed to fetch phone data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };
  
  const handleAssignQueue = async (itemId, agentId) => {
    try {
      const res = await fetch(`${API_URL}/api/phone/support-queue/${itemId}/assign?agent_id=${agentId}`, {
        method: 'PUT'
      });
      if (res.ok) {
        toast.success('Call assigned to you');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to assign call');
    }
  };
  
  const handleResolveQueue = async (itemId, notes) => {
    try {
      const res = await fetch(`${API_URL}/api/phone/support-queue/${itemId}/resolve?notes=${encodeURIComponent(notes)}`, {
        method: 'PUT'
      });
      if (res.ok) {
        toast.success('Call resolved');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to resolve call');
    }
  };
  
  const handleCompleteCallback = async (msgId, notes) => {
    try {
      const res = await fetch(`${API_URL}/api/phone/callback-messages/${msgId}/complete?notes=${encodeURIComponent(notes)}`, {
        method: 'PUT'
      });
      if (res.ok) {
        toast.success('Callback completed');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to complete callback');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="phone-system">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center">
            <Phone className="w-6 h-6 mr-2 text-neon-cyan" />
            Phone System
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            AI-powered voice support with Tier 1/2 escalation
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-secondary flex items-center"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={PhoneCall}
          label="Today's Calls"
          value={stats?.today_calls || 0}
          color="neon-cyan"
        />
        <StatCard
          icon={Bot}
          label="AI Handled"
          value={`${100 - (stats?.escalation_rate || 0)}%`}
          color="neon-purple"
          subtext="Tier 1 resolution"
        />
        <StatCard
          icon={PhoneIncoming}
          label="Queue Waiting"
          value={stats?.queue_waiting || 0}
          color="yellow-500"
        />
        <StatCard
          icon={MessageSquare}
          label="Pending Callbacks"
          value={stats?.pending_callbacks || 0}
          color="purple-500"
        />
      </div>
      
      {/* Tabs */}
      <div className="flex space-x-1 bg-white/5 p-1 rounded-lg">
        {[
          { id: 'overview', label: 'Overview', icon: Phone },
          { id: 'queue', label: 'Support Queue', icon: Users, badge: stats?.queue_waiting },
          { id: 'callbacks', label: 'Callbacks', icon: MessageSquare, badge: stats?.pending_callbacks },
          { id: 'history', label: 'Call History', icon: History },
          { id: 'config', label: 'Configuration', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
              activeTab === tab.id
                ? 'bg-neon-cyan text-slate-900'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
            {tab.badge > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-slate-900 text-neon-cyan' : 'bg-red-500 text-white'
              }`}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Content */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Support Queue Preview */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-yellow-400" />
              Support Queue
              {supportQueue.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
                  {supportQueue.length} waiting
                </span>
              )}
            </h3>
            {supportQueue.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No calls in queue</p>
              </div>
            ) : (
              <div className="space-y-3">
                {supportQueue.slice(0, 3).map(item => (
                  <QueueItem
                    key={item.id}
                    item={item}
                    onAssign={handleAssignQueue}
                    onResolve={handleResolveQueue}
                  />
                ))}
                {supportQueue.length > 3 && (
                  <button
                    onClick={() => setActiveTab('queue')}
                    className="w-full py-2 text-neon-cyan text-sm hover:underline"
                  >
                    View all {supportQueue.length} items →
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Recent Calls */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <History className="w-5 h-5 mr-2 text-neon-purple" />
              Recent Calls
            </h3>
            {callLogs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <PhoneOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No calls yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {callLogs.slice(0, 5).map(call => (
                  <CallLogItem key={call.id} call={call} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'queue' && (
        <div className="space-y-4">
          {supportQueue.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400 opacity-50" />
              <h3 className="text-lg font-semibold text-white mb-2">Queue is Clear</h3>
              <p className="text-slate-400">No customers waiting for support</p>
            </div>
          ) : (
            supportQueue.map(item => (
              <QueueItem
                key={item.id}
                item={item}
                onAssign={handleAssignQueue}
                onResolve={handleResolveQueue}
              />
            ))
          )}
        </div>
      )}
      
      {activeTab === 'callbacks' && (
        <div className="space-y-4">
          {callbacks.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400 opacity-50" />
              <h3 className="text-lg font-semibold text-white mb-2">All Caught Up</h3>
              <p className="text-slate-400">No pending callbacks</p>
            </div>
          ) : (
            callbacks.map(msg => (
              <CallbackItem
                key={msg.id}
                message={msg}
                onComplete={handleCompleteCallback}
              />
            ))
          )}
        </div>
      )}
      
      {activeTab === 'history' && (
        <div className="space-y-4">
          {callLogs.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <PhoneOff className="w-16 h-16 mx-auto mb-4 text-slate-500 opacity-50" />
              <h3 className="text-lg font-semibold text-white mb-2">No Call History</h3>
              <p className="text-slate-400">Calls will appear here once received</p>
            </div>
          ) : (
            callLogs.map(call => (
              <CallLogItem key={call.id} call={call} />
            ))
          )}
        </div>
      )}
      
      {activeTab === 'config' && (
        <ConfigPanel config={config} onRefresh={fetchData} />
      )}
    </div>
  );
};

export default PhoneSystem;
